import React from 'react';
import axios from "axios";
import { useEffect, useState } from "react";
import config from "../../config";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import loadingImg from "../../assets/loading.gif"

const IndexDataImport = () => {
    const companyId = useSelector((state) => state.user.companyId);
    const [err, setErr] = useState("");
    const [errSuccess, setErrSuccess] = useState("");
    const [errCash, setErrCash] = useState("");
    const [errCashSuccess, setErrCashSuccess] = useState("");
    const [file, setFile] = useState(null);
    const [cashFile, setCashFile] = useState(null);
    const [isloading, setIsloading] = useState(false)
    const [isCashLoading, setIsCashLoading] = useState(false)
    const [cashPreviewHeader, setCashPreviewHeader] = useState([]);
    const [cashPreviewRows, setCashPreviewRows] = useState([]);
    const [cashValidationErrors, setCashValidationErrors] = useState([]);
    const [isCashFileValid, setIsCashFileValid] = useState(false);

    const expectedCashBalanceHeaders = ["clientCode", "productCode", "amount"];
    const maxCashPreviewRows = 5;
    const supportedCashExtensions = ["csv", "xlsx", "xls"];

    const downloadCashTemplateCsv = () => {
        const sampleRow = ["ABC123", "TV", "1234.56"];
        const csv = `${expectedCashBalanceHeaders.join(",")}\n${sampleRow.join(",")}\n`;
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "client_cash_balance_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const downloadCashTemplateXlsx = () => {
        const worksheet = XLSX.utils.aoa_to_sheet([
            expectedCashBalanceHeaders,
            ["ABC123", "TV", "1234.56"]
        ]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
        XLSX.writeFile(workbook, "client_cash_balance_template.xlsx");
    };

    const parseCsvLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                result.push(current);
                current = "";
            } else {
                current += char;
            }
        }
        result.push(current);
        return result;
    };

    const validateCashBalanceCsv = (rows) => {
        const errors = [];
        if (!rows || rows.length === 0) {
            errors.push("File is empty or could not be parsed.");
            return errors;
        }

        const normalizeHeader = (value) => value.trim().toLowerCase().replace(/\s+/g, "");
        const header = rows[0].map((cell) => normalizeHeader(String(cell)));
        const expectedNormalizedHeaders = ["clientcode", "productcode", "amount"];
        const headerAliases = {
            clientcode: ["clientcode"],
            productcode: ["productcode"],
            amount: ["amount", "cash"]
        };

        const missingHeaders = expectedNormalizedHeaders.filter((expected) => {
            const aliasList = headerAliases[expected];
            return !aliasList.some((alias) => header.includes(alias));
        });

        if (missingHeaders.length) {
            const displayMissing = missingHeaders.map((name) => {
                return name === "amount" ? "cash/amount" : name === "clientcode" ? "Client Code" : "Product Code";
            });
            errors.push(`Missing header(s): ${displayMissing.join(", ")}`);
        }

        const expectedLength = expectedCashBalanceHeaders.length;
        rows.slice(1).forEach((row, rowIndex) => {
            const lineNumber = rowIndex + 2;
            if (row.length !== expectedLength) {
                errors.push(`Row ${lineNumber} has ${row.length} columns but expected ${expectedLength}.`);
                return;
            }

            const clientCode = row[0].trim();
            const productCode = row[1].trim();
            const amount = row[2].trim();

            if (!clientCode) {
                errors.push(`Row ${lineNumber}: clientCode is required.`);
            } else if (!/^[A-Za-z0-9]+$/.test(clientCode)) {
                errors.push(`Row ${lineNumber}: clientCode must be alphanumeric.`);
            }

            if (!productCode) {
                errors.push(`Row ${lineNumber}: productCode is required.`);
            } else if (!/^[A-Za-z]+$/.test(productCode)) {
                errors.push(`Row ${lineNumber}: productCode must be a string.`);
            } 
            
            if (!amount) {
                errors.push(`Row ${lineNumber}: amount is required.`);
            } else if (!/^(?:\d+)(?:\.\d{1,2})?$/.test(amount)) {
                errors.push(`Row ${lineNumber}: amount must be a valid number with up to 2 decimal places.`);
            }
        });

        return errors;
    };

    const loadCashPreview = (file) => {
        const extension = file.name.split('.').pop().toLowerCase();
        if (!supportedCashExtensions.includes(extension)) {
            setErrCash(`Unsupported file type: .${extension}. Use .csv or .xlsx.`);
            setCashPreviewHeader([]);
            setCashPreviewRows([]);
            setCashValidationErrors([`Unsupported file type: .${extension}.`]);
            setIsCashFileValid(false);
            return;
        }

        const processRows = (rows) => {
            if (!rows || rows.length === 0) {
                setCashPreviewHeader([]);
                setCashPreviewRows([]);
                setCashValidationErrors(["File is empty or could not be parsed."]);
                setIsCashFileValid(false);
                return;
            }

            const previewRows = rows.slice(0, maxCashPreviewRows + 1);
            setCashPreviewHeader(previewRows[0] || []);
            setCashPreviewRows(previewRows.slice(1));
            const validationErrors = validateCashBalanceCsv(rows);
            setCashValidationErrors(validationErrors);
            setIsCashFileValid(validationErrors.length === 0);
            if (validationErrors.length === 0) {
                setErrCashSuccess("File format is valid. You may upload now.");
                setErrCash("");
            } else {
                setErrCash("");
                setErrCashSuccess("");
            }
        };

        if (extension === "xlsx" || extension === "xls") {
            const reader = new FileReader();
            reader.onload = (event) => {
                const arrayBuffer = event.target.result;
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, defval: "" });
                processRows(rows);
            };
            reader.onerror = () => {
                setErrCash("Unable to read XLSX file preview.");
                setCashPreviewHeader([]);
                setCashPreviewRows([]);
                setCashValidationErrors(["Unable to read XLSX file."]);
                setIsCashFileValid(false);
            };
            reader.readAsArrayBuffer(file);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target.result;
            const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
            const lines = normalized.split("\n").filter((line) => line.trim() !== "");
            const rows = lines.map(parseCsvLine);
            processRows(rows);
        };
        reader.onerror = () => {
            setErrCash("Unable to read file preview.");
            setCashPreviewHeader([]);
            setCashPreviewRows([]);
            setCashValidationErrors(["Unable to read file."]);
            setIsCashFileValid(false);
        };
        reader.readAsText(file);
    };

    const uploadCash = async () => {

        setErrCashSuccess("");
        setErrCash("");
        if (!cashFile) {
            setErrCash("Please select a cash balance file before uploading.");
            return;
        }
        if (!isCashFileValid) {
            setErrCash("File format is invalid. Fix the preview errors before uploading.");
            return;
        }

        setIsCashLoading(true)
        const formData = new FormData();
        formData.append("file", cashFile);

        axios.post(config.BL_API_URL + "/clientCashBalanceUpload", formData).then((res) => {
            setIsCashLoading(false);
            console.log(res.data)
            setErrCashSuccess(res.data);
        }).catch((err) => {
            setIsCashLoading(false);
            setErrCash("Error uploading file " + err);
        });



    };

    const upload = async () => {

        setErrSuccess("");
        setErr("");
        setIsloading(true)
        const formData = new FormData();


        formData.append("file", file);

        axios.post(config.BL_API_URL + "/indexDataUpload", formData).then((res) => {
            setIsloading(false);
            console.log(res.data)
            setErrSuccess(res.data);
        }).catch((err) => {
            setIsloading(false);
            setErr("Error uploading file " + err);
        });



    };    

    const handleCashFileSelection = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setCashFile(null);
            setCashPreviewHeader([]);
            setCashPreviewRows([]);
            setCashValidationErrors([]);
            setIsCashFileValid(false);
            return;
        }

        setCashFile(file);
        loadCashPreview(file);
    };

    const handleFileSelection = (e) => {
        const file = e.target.files[0];

        setFile(file);
    };

    const indexFeedbackMessages = [];
    if (err) indexFeedbackMessages.push({ type: 'error', text: err });
    if (errSuccess) indexFeedbackMessages.push({ type: 'success', text: errSuccess });

    const cashValidationMessages = cashValidationErrors.filter(Boolean);
    const cashFeedbackMessages = [];
    if (errCash) cashFeedbackMessages.push({ type: 'error', text: errCash });
    cashValidationMessages.forEach((message) => cashFeedbackMessages.push({ type: 'error', text: message }));
    if (errCashSuccess) cashFeedbackMessages.push({ type: 'success', text: errCashSuccess });

    const shouldShowSummary = indexFeedbackMessages.length > 0 || cashFeedbackMessages.length > 0;

    return (
        <div>
            <div className="my-14 md:px-14 p-2 max-w-screen-xl mx-auto ">
                <div className="flex flex-col">
                    <div className="-m-1.5 overflow-x-auto">
                        <div className="border rounded-lg divide-y divide-gray-200 py-10">
                            <div className='col-span-2'>
                                <div className=" col-span-2">                                    
                                    <div className="overflow-hidden">
                                        <div className="overflow-hidden">
                                            <table className="min-w-full divide-y divide-gray-200 justify-center  ">
                                                <thead className="bg-gray-50 ">
                                                    <tr>
                                                        <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase"></th>
                                                        <th scope="col" className="px-6 py-2 text-start text-xs font-medium text-gray-500 uppercase"></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    <tr>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            Upload Index Data

                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                <div className='row-span-1 flex justify-left py-1 '>
                                                                    <div className='col-span-1'>
                                                                        <input type="file" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" onChange={e => handleFileSelection(e)} />
                                                                    </div>
                                                                </div>

                                                                <div className='row-span-1 flex justify-left py-1'>
                                                                    <div className='col-span-1'>
                                                                        <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" onClick={upload}>
                                                                            Upload
                                                                            <span className="flex gap-2 items-center justify-left">
                                                                                {isloading && <img src={loadingImg} alt="Icon" className="w-6 text-left" />}
                                                                            </span>
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            Upload Client Cash Balance

                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                <div className='row-span-1 flex flex-wrap gap-3 justify-left py-1 '>
                                                                    <div className='col-span-1'>
                                                                        <input type="file" accept=".csv,.xlsx" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" onChange={e => handleCashFileSelection(e)} />
                                                                    </div>
                                                                    <div className='col-span-1 flex gap-2'>
                                                                        <button className="bg-slate-500 hover:bg-slate-600 text-white font-bold py-2 px-4 rounded" onClick={downloadCashTemplateCsv} type="button">Download CSV template</button>
                                                                        <button className="bg-slate-500 hover:bg-slate-600 text-white font-bold py-2 px-4 rounded" onClick={downloadCashTemplateXlsx} type="button">Download XLSX template</button>
                                                                    </div>
                                                                </div>

                                                                <div className='row-span-1 flex justify-left py-1'>
                                                                    <div className='col-span-1'>
                                                                        <button
                                                                            className={`text-white font-bold py-2 px-4 rounded ${cashFile && isCashFileValid ? 'bg-blue-500 hover:bg-blue-700 cursor-pointer' : 'bg-slate-300 cursor-not-allowed'}`}
                                                                            onClick={uploadCash}
                                                                            disabled={!cashFile || !isCashFileValid}
                                                                        >
                                                                            Upload
                                                                            <span className="flex gap-2 items-center justify-left">
                                                                                {isCashLoading && <img src={loadingImg} alt="Icon" className="w-6 text-left" />}
                                                                            </span>
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {cashPreviewHeader.length > 0 && (
                                                                    <div className='row-span-1 flex flex-col py-2'>
                                                                        <div className='text-sm font-semibold text-gray-700 mb-2'>Preview ({Math.min(cashPreviewRows.length, maxCashPreviewRows)} rows)</div>
                                                                        <div className='overflow-x-auto border rounded-lg p-2 bg-gray-50'>
                                                                            <table className='min-w-full text-sm'>
                                                                                <thead>
                                                                                    <tr>
                                                                                        {cashPreviewHeader.map((col, idx) => (
                                                                                            <th key={idx} className='px-2 py-1 text-left font-semibold text-gray-600'>{col}</th>
                                                                                        ))}
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {cashPreviewRows.map((row, rowIndex) => (
                                                                                        <tr key={rowIndex} className='border-t'>
                                                                                            {cashPreviewHeader.map((_, colIndex) => (
                                                                                                <td key={colIndex} className='px-2 py-1 text-gray-800'>{row[colIndex] ?? ""}</td>
                                                                                            ))}
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>

                                            {shouldShowSummary && (
                                                <div className="sticky bottom-0 z-20 mt-6 rounded-t-xl border-t border-slate-200 bg-slate-50/95 p-4 text-sm backdrop-blur-sm shadow-sm">
                                                    <div className="mb-4 text-base font-semibold text-slate-900">Upload feedback summary</div>
                                                    <div className="space-y-4">
                                                        {indexFeedbackMessages.length > 0 && (
                                                            <div className="grid gap-3 sm:grid-cols-[120px_1fr] items-start rounded-lg bg-white p-3 shadow-sm">
                                                                <div className="font-semibold text-slate-900">Index</div>
                                                                <div className="space-y-1">
                                                                    {indexFeedbackMessages.map((message, idx) => (
                                                                        <div key={idx} className={message.type === 'error' ? 'text-red-700' : 'text-slate-800'}>
                                                                            {message.type === 'error' ? 'Error:' : 'Success:'} {message.text}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {cashFeedbackMessages.length > 0 && (
                                                            <div className="grid gap-3 sm:grid-cols-[120px_1fr] items-start rounded-lg bg-white p-3 shadow-sm">
                                                                <div className="font-semibold text-slate-900">Cash</div>
                                                                <div className="space-y-1">
                                                                    {cashFeedbackMessages.map((message, idx) => (
                                                                        <div key={idx} className={message.type === 'error' ? 'text-red-700' : 'text-slate-800'}>
                                                                            {message.type === 'error' ? 'Error:' : 'Success:'} {message.text}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default IndexDataImport;